import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  MouseSensor,
  TouchSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import { toast } from 'sonner'
import { Columns, AlertTriangle } from 'lucide-react'
import { api, extractApiError } from '../../lib/api'
import { formatDateTime } from '../../lib/format'
import { invalidateGamification } from '../../lib/gamification-refresh'
import { Breadcrumb } from '../../components/Breadcrumb'
import { boardResponse } from '../../lib/types'

/**
 * /app/board/:listId — drag-drop Kanban over the list's tasks.
 *
 * Columns come from the workflow that applies to this list (resolved
 * server-side via List → Project → workflowId, falling back to the
 * tenant default). WIP-limit overage shows an inline warning but does
 * NOT block the move — the workflow author is the source of truth for
 * what's allowed; WIP is a soft hint.
 */
export function BoardPage() {
  const { listId } = useParams()
  const qc = useQueryClient()
  const [activeDrag, setActiveDrag] = useState(null)

  const boardQuery = useQuery({
    queryKey: ['board', listId],
    queryFn: async () => {
      const res = await api.get(`/lists/${listId}/board`)
      return boardResponse.parse(res.data)
    },
    enabled: Boolean(listId),
  })

  const moveMutation = useMutation({
    mutationFn: async ({ taskId, toStatus }) => {
      await api.patch(`/lists/${listId}/tasks/${taskId}`, { status: toStatus })
    },
    onMutate: async ({ taskId, toStatus }) => {
      await qc.cancelQueries({ queryKey: ['board', listId] })
      const previous = qc.getQueryData(['board', listId])
      // Optimistic move — find the task in any column, drop it into the
      // target column. The server PATCH is what actually persists; this
      // just keeps the UI snappy.
      qc.setQueryData(['board', listId], (cur) => {
        if (!cur) return cur
        const nextColumns = cur.columns.map((c) => ({ ...c, tasks: [...c.tasks] }))
        let moving = null
        for (const c of nextColumns) {
          const i = c.tasks.findIndex((t) => t.id === taskId)
          if (i >= 0) {
            moving = c.tasks.splice(i, 1)[0]
            break
          }
        }
        if (moving) {
          const target =
            nextColumns.find((c) => c.key === toStatus) ?? nextColumns[0]
          target.tasks.unshift({ ...moving, status: toStatus })
        }
        return { ...cur, columns: nextColumns }
      })
      return { previous }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(['board', listId], ctx.previous)
      toast.error(extractApiError(err).message)
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['board', listId] })
      // Drag-to-column is always a status change → refresh points surfaces.
      invalidateGamification(qc)
    },
  })

  // MouseSensor keeps desktop behaviour identical (small activation distance
  // so a plain click doesn't drag). TouchSensor is delay-based so a swipe
  // scrolls the board while a press-and-hold starts a drag.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 200, tolerance: 8 },
    }),
  )

  const columns = boardQuery.data?.columns ?? []

  const overWipColumns = useMemo(
    () =>
      new Set(
        columns
          .filter((c) => c.wipLimit != null && c.tasks.length > c.wipLimit)
          .map((c) => c.key),
      ),
    [columns],
  )

  function handleDragEnd(event) {
    setActiveDrag(null)
    const { active, over } = event
    if (!over) return
    const taskId = String(active.id)
    const toStatus = String(over.id)
    // Find current status of the task to skip no-op moves.
    let curStatus = null
    for (const c of columns) {
      if (c.tasks.some((t) => t.id === taskId)) {
        curStatus = c.key
        break
      }
    }
    if (curStatus === toStatus) return
    moveMutation.mutate({ taskId, toStatus })
  }

  return (
    <div className="h-full flex flex-col">
      <Breadcrumb current="Board" />
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="font-display text-2xl text-[color:var(--color-ink-strong)] flex items-center gap-2">
            <Columns size={20} className="text-[color:var(--color-accent-strong)]" />
            Kanban Board
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-ink-muted)]">
            Drag tasks across a kanban board.
          </p>
        </div>
        {boardQuery.data && (
          <div className="text-xs text-[color:var(--color-ink-muted)]">
            Workflow: <span className="font-medium">{boardQuery.data.workflow.name}</span>
          </div>
        )}
      </div>

      {boardQuery.isLoading ? (
        <div className="text-[color:var(--color-ink-muted)]">Loading board…</div>
      ) : boardQuery.isError ? (
        <div className="text-red-600 text-sm">
          {extractApiError(boardQuery.error).message}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          onDragStart={(e) => setActiveDrag(String(e.active.id))}
          onDragEnd={handleDragEnd}
          onDragCancel={() => setActiveDrag(null)}
        >
          <div className="flex-1 overflow-x-auto">
            <div className="flex gap-3 min-h-full">
              {columns.map((col) => (
                <BoardColumn
                  key={col.key}
                  column={col}
                  overWip={overWipColumns.has(col.key)}
                  activeDrag={activeDrag}
                />
              ))}
            </div>
          </div>
        </DndContext>
      )}
    </div>
  )
}

function BoardColumn({ column, overWip, activeDrag }) {
  const { setNodeRef, isOver } = useDroppable({ id: column.key })
  return (
    <div
      ref={setNodeRef}
      className={`w-72 shrink-0 rounded-[10px] border bg-[color:var(--color-canvas)] flex flex-col ${
        isOver
          ? 'border-[color:var(--color-accent)] ring-2 ring-[color:var(--color-accent)]/30'
          : 'border-[color:var(--color-rule)]'
      }`}
    >
      <div
        className="px-3 py-2 border-b border-[color:var(--color-rule)] flex items-center justify-between gap-2 sticky top-0 z-10"
        style={{ backgroundColor: `${column.color}14` }}
      >
        <div className="flex items-center gap-2 min-w-0">
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ backgroundColor: column.color || '#94a3b8' }}
          />
          <span className="text-xs font-semibold text-[color:var(--color-ink-strong)] truncate uppercase tracking-wider">
            {column.label}
          </span>
        </div>
        <div className="flex items-center gap-2 text-[12px] text-[color:var(--color-ink-muted)]">
          {column.wipLimit != null && (
            <span className={overWip ? 'text-red-600 font-medium' : ''}>
              {column.tasks.length}/{column.wipLimit}
            </span>
          )}
          {column.wipLimit == null && <span>{column.tasks.length}</span>}
          {overWip && <AlertTriangle size={12} className="text-red-600" />}
        </div>
      </div>
      <div className="flex-1 overflow-y-auto p-2 space-y-2 min-h-[60vh]">
        {column.tasks.length === 0 ? (
          <div className="text-[12px] text-[color:var(--color-ink-muted)] text-center py-6">
            Drop tasks here
          </div>
        ) : (
          column.tasks.map((task) => (
            <DraggableCard
              key={task.id}
              task={task}
              dimmed={activeDrag === task.id}
            />
          ))
        )}
      </div>
    </div>
  )
}

function DraggableCard({ task, dimmed }) {
  // Moving a card between status columns is a status change, so the card is
  // only draggable when the viewer is allowed to change this task's status.
  const dragDisabled = task.canEditStatus === false
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id: task.id,
    disabled: dragDisabled,
  })
  const style = transform
    ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)` }
    : undefined
  return (
    <div
      ref={setNodeRef}
      style={style}
      {...listeners}
      {...attributes}
      className={`rounded-[8px] bg-[color:var(--color-card)] border border-[color:var(--color-rule)] px-3 py-2 ${
        dragDisabled ? 'cursor-default' : 'cursor-grab active:cursor-grabbing'
      } hover:shadow-sm transition-shadow ${dimmed ? 'opacity-40' : ''}`}
    >
      <div className="text-sm text-[color:var(--color-ink-strong)] line-clamp-2">
        {task.name}
      </div>
      <div className="flex items-center gap-1.5 mt-2 text-[10px] text-[color:var(--color-ink-muted)]">
        {task.priority && (
          <span className="uppercase font-medium">{task.priority}</span>
        )}
        {task.dueDate && (
          // Canonical DD/MM/YYYY HH:MM AM/PM per `lib/format.js`.
          <span>{formatDateTime(task.dueDate)}</span>
        )}
        {task.assigneeIds.length > 0 && (
          <span className="ml-auto">
            {task.assigneeIds.length} assignee
            {task.assigneeIds.length === 1 ? '' : 's'}
          </span>
        )}
      </div>
    </div>
  )
}
